/**
 * Sentry Event Scrubber — the code-level PII guarantee (productai-error-tracking-driver A-4).
 *
 * Candidate- and consumer-facing surfaces are default-deny on personal data.
 * Apply/join flows carry names, emails, LinkedIn URLs, resumes — none of that
 * may travel inside an error envelope. This module is the versioned, tested
 * enforcement of that rule: the `beforeSend` hook (wired at the bottom of this
 * file) runs the scrubber on every event, so a Sentry dashboard reset can never
 * silently disable scrubbing — the guarantee lives in code and in CI.
 *
 * The scrub functions are pure (no Sentry import) so they unit-test without the
 * SDK. The browser-only wiring at the bottom (window.sentryOnLoad) is built to a
 * classic synchronous script and emitted in page-head before the hosted loader;
 * in Node/test it is a no-op (guarded on `typeof window`).
 */

/** Matches email addresses anywhere in a string. */
const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;

/**
 * Key fragments whose values are always redacted. Matched as case-insensitive
 * substrings, so `email` also catches `candidate_email`, `linkedin` catches
 * `linkedin_url`, etc. This deliberately over-redacts (e.g. `event_name` →
 * redacted via `name`): in `beforeSend`, dropping a little debug context is
 * always safer than leaking PII. The tradeoff is asserted in the test suite.
 */
const PII_KEYS = [
  'email',
  'name',
  'firstname',
  'lastname',
  'first_name',
  'last_name',
  'phone',
  'linkedin',
  'linkedin_url',
  'resume',
  'portfolio',
  'github',
  'token',
  // 'authorization' not bare 'auth': bare 'auth' also swallowed author /
  // authorized / authenticated, which aren't PII and are useful debug signal.
  // Credential values are still caught via 'token' (auth_token, access_token),
  // 'auth_code' (short-lived OAuth codes on apply/join flows), and 'password'.
  'authorization',
  'auth_code',
  'password',
];

const REDACTED = '[redacted]';

/** Guard against pathological/cyclic nesting in breadcrumb/context bags. */
const MAX_DEPTH = 6;

/** Replace email addresses embedded in free text (exception messages, breadcrumbs). */
export function scrubText(text: string): string {
  return text.replace(EMAIL_RE, REDACTED);
}

/** Strip the query string and fragment from a URL — params on apply flows can carry PII. */
export function scrubUrl(url: string): string {
  const cut = (s: string, ch: string): string => {
    const i = s.indexOf(ch);
    return i === -1 ? s : s.slice(0, i);
  };
  return cut(cut(url, '?'), '#');
}

function isPiiKey(key: string): boolean {
  const k = key.toLowerCase();
  return PII_KEYS.some((p) => k.includes(p));
}

/**
 * Redact PII-keyed entries and email-shaped values in a record. Recurses into
 * nested objects and arrays so PII buried under a benign key (e.g.
 * `{ applicant: { email: 'a@b.co' } }`) is still scrubbed.
 */
export function scrubRecord(
  record: Readonly<Record<string, unknown>>,
  depth = 0
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    // depth is incremented only when scrubValue recurses back into scrubRecord
    // (object/array boundary), not here — so MAX_DEPTH counts nested *object*
    // levels, which is the only place runaway nesting comes from.
    out[key] = isPiiKey(key) ? REDACTED : scrubValue(value, depth);
  }
  return out;
}

function scrubValue(value: unknown, depth: number): unknown {
  if (typeof value === 'string') {
    return scrubText(value);
  }
  if (depth >= MAX_DEPTH || value === null || typeof value !== 'object') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((v) => scrubValue(v, depth + 1));
  }
  return scrubRecord(value as Record<string, unknown>, depth + 1);
}

/**
 * Structural type for the slice of a Sentry `Event` we scrub. Typed structurally
 * rather than against @sentry/browser's `Event` so the module stays
 * SDK-independent and testable; sentry-init.ts narrows the real event to this.
 */
export interface ScrubbableEvent {
  request?: { url?: string; headers?: Record<string, unknown> };
  breadcrumbs?: Array<{
    message?: string;
    data?: Record<string, unknown>;
  }>;
  exception?: {
    values?: Array<{ value?: string }>;
  };
  message?: string;
  user?: unknown;
  /** Free-form debug bag — Phase 2 apply/join instrumentation lands PII here. */
  extra?: Record<string, unknown>;
  /** Structured context bags (e.g. Sentry.setContext('candidate', {...})). */
  contexts?: Record<string, Record<string, unknown>>;
}

export function scrubEvent<T extends ScrubbableEvent>(event: T): T {
  // Never attach user identity from the client (driver A-4: default-deny).
  if ('user' in event) {
    delete event.user;
  }

  if (event.request?.url) {
    event.request.url = scrubUrl(event.request.url);
  }
  if (event.request?.headers) {
    event.request.headers = scrubRecord(event.request.headers);
  }

  if (event.message) {
    event.message = scrubText(event.message);
  }

  if (event.exception?.values) {
    for (const ex of event.exception.values) {
      if (ex.value) {
        ex.value = scrubText(ex.value);
      }
    }
  }

  if (event.breadcrumbs) {
    for (const crumb of event.breadcrumbs) {
      if (crumb.message) {
        crumb.message = scrubText(crumb.message);
      }
      if (crumb.data) {
        crumb.data = scrubRecord(crumb.data);
      }
    }
  }

  // Phase 2 forward-coverage: setExtra / setContext on apply/join flows carry
  // exactly the PII in PII_KEYS — scrub them now so the guarantee holds the
  // moment that instrumentation lands.
  if (event.extra) {
    event.extra = scrubRecord(event.extra);
  }
  if (event.contexts) {
    for (const [key, bag] of Object.entries(event.contexts)) {
      event.contexts[key] = scrubRecord(bag);
    }
  }

  return event;
}

// --- Browser wiring -------------------------------------------------------
// The hosted Sentry loader calls window.sentryOnLoad (if defined before it
// runs) instead of auto-initing, letting us install the tested beforeSend.
// Defining sentryOnLoad means the loader no longer injects the DSN, so we
// supply it explicitly in the init call below (see the DSN comment there).
declare global {
  interface Window {
    Sentry?: { init: (options: Record<string, unknown>) => void };
    sentryOnLoad?: () => void;
  }
}

if (typeof window !== 'undefined') {
  window.sentryOnLoad = function sentryOnLoad(): void {
    window.Sentry?.init({
      // Explicit DSN: when sentryOnLoad is defined the loader hands init to us
      // and does NOT inject the DSN, so omitting it silently drops every event.
      // Public-by-design key (same one in the loader URL); ingest host is the
      // org's own o1095749.ingest.us.sentry.io — kept in lockstep with the
      // connect-src CSP pin in worker/index.ts.
      dsn: 'https://0b2f6fe0a5edc8fbc4ac23f9daa71f46@o1095749.ingest.us.sentry.io/4511554026143744',
      sendDefaultPii: false,
      beforeSend: (event: ScrubbableEvent) => scrubEvent(event),
    });
  };
}
